import { z } from 'zod';
import {
  LoginFlowSchema,
  RegistrationFlowSchema,
  RecoveryFlowSchema,
  VerificationFlowSchema,
  SessionSchema,
  IdentityTraitsSchema,
} from '@/lib/api/kratos';

// ============================================================================
// Login Schemas
// ============================================================================

/**
 * Schema for login form values - flexible for different login steps
 */
export const LoginFormValuesSchema = z.object({
  method: z.string().min(1, 'Method is required'),
  identifier: z.string().optional(), // Optional for some steps
  password: z.string().optional(), // Optional for identifier_first step
  csrf_token: z.string().optional(),
}).refine((data) => {
  // For identifier_first method, only identifier is required
  if (data.method === 'identifier_first') {
    return data.identifier && data.identifier.length > 0;
  }
  // For password method, both identifier and password are required
  if (data.method === 'password') {
    return data.identifier && data.password && data.password.length > 0;
  }
  // For other methods, method is sufficient
  return true;
}, {
  message: 'Invalid form data for the selected method',
});

export type LoginFormValues = z.infer<typeof LoginFormValuesSchema>;

/**
 * Schema for login response - can be either a session or an updated flow
 */
export const LoginResponseSchema = z.object({
  session: SessionSchema.optional(),
  flow: LoginFlowSchema.optional(),
});

export type LoginResponse = z.infer<typeof LoginResponseSchema>;

// ============================================================================
// Registration Schemas
// ============================================================================

/**
 * Schema for registration form values
 */
export const RegistrationFormValuesSchema = z.object({
  method: z.string().min(1, 'Method is required'),
  traits: IdentityTraitsSchema,
  password: z.string().min(8, 'Password must be at least 8 characters'),
  csrf_token: z.string().optional(),
});

export type RegistrationFormValues = z.infer<typeof RegistrationFormValuesSchema>;

/**
 * Schema for registration response - can be either a session or an updated flow
 */
export const RegistrationResponseSchema = z.object({
  session: SessionSchema.optional(),
  flow: RegistrationFlowSchema.optional(),
  identity: z.object({
    id: z.string().uuid(),
  }).optional(),
});

export type RegistrationResponse = z.infer<typeof RegistrationResponseSchema>;

// ============================================================================
// Recovery Schemas
// ============================================================================

/**
 * Schema for recovery form values (password reset)
 */
export const RecoveryFormValuesSchema = z.object({
  method: z.string().min(1, 'Method is required'),
  email: z.string().email('Invalid email address').optional(),
  code: z.string().optional(),
  password: z.string().optional(),
  csrf_token: z.string().optional(),
});

export type RecoveryFormValues = z.infer<typeof RecoveryFormValuesSchema>;

/**
 * Schema for recovery response
 */
export const RecoveryResponseSchema = z.object({
  flow: RecoveryFlowSchema.nullable(),
  state: z.enum(['choose_method', 'sent_email', 'passed_challenge', 'error']).optional(),
  redirect_url: z.string().optional(),
});

export type RecoveryResponse = z.infer<typeof RecoveryResponseSchema>;

// ============================================================================
// Verification Schemas
// ============================================================================

/**
 * Schema for verification form values (email verification)
 */
export const VerificationFormValuesSchema = z.object({
  method: z.string().min(1, 'Method is required'),
  email: z.string().email('Invalid email address').optional(),
  code: z.string().optional(),
  csrf_token: z.string().optional(),
});

export type VerificationFormValues = z.infer<typeof VerificationFormValuesSchema>;

/**
 * Schema for verification response
 */
export const VerificationResponseSchema = z.object({
  flow: VerificationFlowSchema,
  state: z.enum(['choose_method', 'sent_email', 'passed_challenge']).optional(),
});

export type VerificationResponse = z.infer<typeof VerificationResponseSchema>;

// ============================================================================
// Logout Schemas
// ============================================================================

/**
 * Schema for logout response
 */
export const LogoutResponseSchema = z.object({
  logout_token: z.string(),
  logout_url: z.string().url(),
});

export type LogoutResponse = z.infer<typeof LogoutResponseSchema>;

// ============================================================================
// Session Validation Schemas
// ============================================================================

/**
 * Schema for session validation response
 */
export const SessionValidationResponseSchema = z.object({
  session: SessionSchema,
  isValid: z.boolean(),
});

export type SessionValidationResponse = z.infer<typeof SessionValidationResponseSchema>;

// ============================================================================
// Error Schemas
// ============================================================================

/**
 * Schema for authentication error response
 */
export const AuthErrorSchema = z.object({
  error: z.object({
    id: z.string(),
    code: z.number().optional(),
    status: z.string().optional(),
    reason: z.string().optional(),
    message: z.string(),
    details: z.record(z.unknown()).optional(),
  }),
});

export type AuthError = z.infer<typeof AuthErrorSchema>;

/**
 * Schema for validation error details
 */
export const ValidationErrorDetailsSchema = z.object({
  field: z.string(),
  messages: z.array(z.string()),
});

export type ValidationErrorDetails = z.infer<typeof ValidationErrorDetailsSchema>;

/**
 * Schema for form validation errors
 */
export const FormValidationErrorSchema = z.object({
  errors: z.array(ValidationErrorDetailsSchema),
  message: z.string(),
});

export type FormValidationError = z.infer<typeof FormValidationErrorSchema>;
